import { useCallback, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AccountPanel } from './components/AccountPanel'
import { FrontDoor } from './front/FrontDoor'
import type { FrontPage } from './front/FrontDoor'
import { PrivacyPage } from './front/PrivacyPage'
import { loadDays, pullAccountSettings, reckon, reckonToday } from './calendar'
import { CalendarPage } from './calendar/CalendarPage'
import { ForumPanel } from './forum/ForumPanel'
import { PrayerSide } from './forum/PrayerSide'
import type { Reckoning } from './calendar'
import { applyMode, storedMode } from './theme'
import type { Mode } from './theme'
import { PrivateConsole } from './ConsoleShell'
import { useSession } from './useSession'
import { formatDate, isMember } from './session'
import { Link } from 'react-router-dom'
import { TopBar, VIEW_PATH, viewOf } from './shell/TopBar'
import type { View } from './shell/TopBar'
import { Ribbon } from './shell/Ribbon'
import { ToastProvider } from './shell/Toast'
import { AppointedTimeCard, AppointedTimes, MonthWidget, PhoneWidget } from './shell/Widgets'
import { Feed, RecipeModal, firstName, initialOf } from './feed/Feed'
import { SearchModal } from './feed/SearchModal'
import { useMembersData } from './feed/useMembersData'
import type { Recipe } from './recipes'

/**
 * The members' site, one real path per view (/recipes, /sisters, /privacy), the way the Torahly site
 * does it. The server hands every path that is not a file `index.html` (see htaccess.template) and
 * React Router reads the URL from there. The admin panel is not one of these: it has its own entry
 * point (`admin.html`) and nothing here links to it. The private portal is a members' surface that
 * asks for no account: `/phone` is reached by a woman pairing her own phone.
 */

/** Links given out when this was one page with hash routes still land on the right path. */
const MOVED_HASH: Record<string, string> = {
  '#table': VIEW_PATH.table,
  '#sisters': VIEW_PATH.sisters,
  '#calendar': VIEW_PATH.calendar,
  '#account': VIEW_PATH.account,
  '#phone': VIEW_PATH.phone,
  '#keepers': VIEW_PATH.keepers,
}

export function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <Site />
    </BrowserRouter>
  )
}

function Site() {
  const { user, loading, offline, signOut } = useSession()
  const [mode, setMode] = useState<Mode>(storedMode)
  const [reckoning, setReckoning] = useState<Reckoning | null>(null)
  const { pathname, hash } = useLocation()
  const navigate = useNavigate()
  const servedByPhone = new URLSearchParams(window.location.search).has('phone')
  // `?phone` is the app serving this bundle: the private portal, whatever path it was opened at.
  const view = servedByPhone ? 'phone' : viewOf(pathname)

  useEffect(() => {
    const moved = pathname === '/' ? MOVED_HASH[hash] : pathname === '/table' ? VIEW_PATH.table : undefined
    if (moved) navigate(moved, { replace: true })
    else if (!hash) window.scrollTo(0, 0)
  }, [pathname, hash, navigate])

  useEffect(() => {
    applyMode(mode)
  }, [mode])

  // On "system", follow the OS if it changes while the tab is open.
  useEffect(() => {
    if (mode !== 'system') return
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyMode('system')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [mode])

  // The calendar is public, so this runs signed out too and the front door opens on a real date.
  const userId = user?.id ?? null
  const [pulledFor, setPulledFor] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    // A signed-in member's year-start reading and location come from their Torahly account first,
    // so the dates match what they see in Torahly.
    const settings = userId ? pullAccountSettings(userId) : Promise.resolve()
    settings.then(() => {
      if (!cancelled) setPulledFor(userId)
    })
    // Same days and the same sunset turnover as the Calendar page, so the ribbon and the page
    // always agree on what day it is.
    settings
      .then(() => loadDays())
      .then(async (days) => {
        const today = await reckonToday(days)
        if (!cancelled) setReckoning(reckon(days, today?.day.d))
      })
      .catch(() => {
        /* a missing calendar hides the date, which is quieter than an error nobody can act on */
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  if (loading || pulledFor !== userId) {
    return <p className="quiet-center">One moment</p>
  }

  if (view === null) return <Navigate to="/" replace />
  if (view === 'signin' && user) return <Navigate to="/" replace />

  /*
   * The private portal sits in front of the sign-in gate on purpose.
   *
   * It reads her phone and nothing else, so requiring an account would be asking her to identify
   * herself to a server in order to look at data that server is never going to receive.
   */
  if (view === 'phone') {
    return (
      <ToastProvider>
        {!servedByPhone && (
          <TopBar
            view="phone"
            signedIn={!!user}
            initial={initialOf(user?.name || user?.email || 'S')}
            mode={mode}
            onMode={setMode}
            onSignOut={signOut}
          />
        )}
        <PrivateConsole />
      </ToastProvider>
    )
  }

  if (view === 'privacy') {
    return (
      <ToastProvider>
        <TopBar
          view="privacy"
          signedIn={!!user}
          initial={initialOf(user?.name || user?.email || 'S')}
          mode={mode}
          onMode={setMode}
          onSignOut={signOut}
        />
        <PrivacyPage />
        <Footer />
      </ToastProvider>
    )
  }

  if (!user) {
    const page = view === 'sisters' || view === 'account' ? 'locked' : view
    return (
      <ToastProvider>
        <TopBar view={view} signedIn={false} initial="" mode={mode} onMode={setMode} />
        <Ribbon reckoning={reckoning} name={null} />
        {view === 'calendar' ? (
          <main className="wrap cal-main">
            <CalendarPage />
          </main>
        ) : (
          <FrontDoor page={page as FrontPage} reckoning={reckoning} />
        )}
        <Footer />
      </ToastProvider>
    )
  }

  return (
    <ToastProvider>
      <Members user={user} offline={offline} view={view} mode={mode} onMode={setMode} reckoning={reckoning} onSignOut={signOut} />
      <Footer />
    </ToastProvider>
  )
}

function Members({
  user,
  offline,
  view,
  mode,
  onMode,
  reckoning,
  onSignOut,
}: {
  user: NonNullable<ReturnType<typeof useSession>['user']>
  offline: boolean
  view: View
  mode: Mode
  onMode: (mode: Mode) => void
  reckoning: Reckoning | null
  onSignOut: () => void
}) {
  const data = useMembersData(user.id)
  const onPrayers = useLocation().pathname.startsWith('/sisters/prayers')
  const navigate = useNavigate()
  const [searching, setSearching] = useState(false)
  const [openRecipe, setOpenRecipe] = useState<Recipe | null>(null)
  const name = user.name || user.email
  const member = isMember(user)
  const until = user.covella_member_until ? formatDate(user.covella_member_until) : null

  const openSearch = useCallback(() => setSearching(true), [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearching(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <TopBar
        view={view}
        signedIn
        initial={initialOf(name)}
        mode={mode}
        onMode={onMode}
        onSearch={openSearch}
        onAdd={() => {
          navigate(VIEW_PATH.table)
        }}
        onSignOut={onSignOut}
      />
      <Ribbon
        reckoning={reckoning}
        name={firstName(user.name) || null}
        memberLine={member ? (until ? `Keeper, through ${until}` : 'Keeper') : 'Not a member'}
      />

      {view === 'calendar' ? (
        <main className="wrap cal-main">
          <CalendarPage />
        </main>
      ) : (
      <main className="wrap main">
        <div className="col-main">
          {data.error && <p className="notice">{data.error}</p>}

          {(view === 'home' || view === 'keepers') && (
            <>
              <AppointedTimeCard reckoning={reckoning} />
              <Feed scope="all" data={data} myUserId={user.id} myName={user.name} daysToSabbath={reckoning?.daysToSabbath ?? null} />
            </>
          )}
          {view === 'table' && (
            <Feed scope="recipes" data={data} myUserId={user.id} myName={user.name} daysToSabbath={reckoning?.daysToSabbath ?? null} />
          )}
          {view === 'sisters' && !onPrayers && <ForumPanel user={user} />}
          {view === 'sisters' && onPrayers && (
            <>
              <p className="forum-crumb">
                <Link to={VIEW_PATH.sisters}>Sisters</Link> / Prayer requests
              </p>
              <Feed scope="prayers" data={data} myUserId={user.id} myName={user.name} daysToSabbath={reckoning?.daysToSabbath ?? null} />
            </>
          )}
          {view === 'account' && <AccountPanel user={user} offline={offline} onSignOut={onSignOut} />}
        </div>

        <aside className="col-side">
          {view === 'sisters' && !onPrayers && <PrayerSide user={user} data={data} />}
          <AppointedTimes
            reckoning={reckoning}
            compact={view === 'sisters'}
            onMonth={() => {
              navigate(VIEW_PATH.calendar)
            }}
          />
          <MonthWidget reckoning={reckoning} compact={view === 'sisters'} />
          {view !== 'sisters' && <PhoneWidget />}
        </aside>
      </main>
      )}

      {searching && <SearchModal data={data} onClose={() => setSearching(false)} onRecipe={setOpenRecipe} />}
      {openRecipe && <RecipeModal recipe={openRecipe} onClose={() => setOpenRecipe(null)} />}
    </>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap">
        <div>
          © {new Date().getFullYear()} Covellah. A sister app of{' '}
          <a href="https://torahly.app">Torahly</a>.
        </div>
        <div>
          <Link to={VIEW_PATH.privacy}>Privacy</Link>
        </div>
      </div>
    </footer>
  )
}
